/**
 * NotificationsPageViewModel - Notification Settings Page Orchestration
 * 
 * Manages complete notification settings page functionality for PageContentNotifications component
 * Handles notification preferences, permission management, and user interactions
 * 
 * Cross-platform implementation with dependency injection
 */

import { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import type {
  IPlatformServices,
  INotificationsPageService,
  NotificationType,
  NotificationSection,
  NotificationPreferences
} from '../contracts'

// Notification section config interface (avoiding @restaurant/core React Native dependencies)
interface NotificationSectionConfig {
  sectionName: string
  notificationDescription: string
  selectedTypes?: ('Push' | 'Text' | 'Email')[]
  onChange?: (sectionName: string, selectedTypes: ('Push' | 'Text' | 'Email')[]) => void
}

// Page content props interface (avoiding @restaurant/core React Native dependencies)
interface PageContentNotificationsProps {
  notificationSections?: NotificationSectionConfig[]
  onNotificationChange?: (allPreferences: Record<string, ('Push' | 'Text' | 'Email')[]>) => void
  className?: string
  testID?: string
  accessibilityLabel?: string
  style?: React.CSSProperties
}

// ViewModel dependencies interface
export interface NotificationsPageViewModelDependencies {
  notificationsPageService?: INotificationsPageService
}

// ViewModel state interface
export interface NotificationsPageViewModelState {
  // Core data
  preferences: NotificationPreferences | null
  notificationSections: NotificationSection[]
  
  // Loading states
  loading: boolean
  error: string | null
  
  // Permission states
  hasPermissions: boolean
  permissionChecked: boolean
  
  // Settings states
  globalEnabled: boolean
  hasUnsavedChanges: boolean
  
  // Computed properties
  sectionCount: number
  enabledNotificationCount: number
  hasAnyNotificationsEnabled: boolean
}

// ViewModel actions interface
export interface NotificationsPageViewModelActions {
  // Preference management
  updateSectionPreferences: (sectionId: string, selectedTypes: NotificationType[]) => Promise<void>
  updateAllPreferences: (preferences: Record<string, NotificationType[]>) => Promise<void>
  toggleNotificationType: (sectionId: string, type: NotificationType, enabled: boolean) => Promise<void>
  isNotificationTypeSelected: (sectionId: string, type: NotificationType) => boolean
  
  // Global settings
  updateGlobalSettings: (enabled: boolean) => Promise<void>
  
  // Permission management
  requestPermissions: () => Promise<boolean>
  refreshPermissions: () => Promise<void>
  
  // Preference utilities
  resetToDefaults: () => Promise<void>
  validateCurrentPreferences: () => boolean
  
  // State management
  clearError: () => void
  refreshData: () => Promise<void>
  
  // Component integration
  getPageContentNotificationsProps: () => Partial<PageContentNotificationsProps>
}

// Combined ViewModel interface
export interface NotificationsPageViewModel extends NotificationsPageViewModelState, NotificationsPageViewModelActions {}

/**
 * Platform-agnostic NotificationsPageViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createNotificationsPageViewModel = (
  _services: IPlatformServices,
  dependencies: NotificationsPageViewModelDependencies = {}
) => {
  return (): NotificationsPageViewModel => {
    // Dependencies
    const { notificationsPageService } = dependencies

    if (!notificationsPageService) {
      throw new Error('NotificationsPageService is required')
    }

    // State management
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null)
  const [notificationSections, setNotificationSections] = useState<NotificationSection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasPermissions, setHasPermissions] = useState(false)
  const [permissionChecked, setPermissionChecked] = useState(false)
  const [globalEnabled, setGlobalEnabled] = useState(true)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Refs for cleanup
  const mountedRef = useRef(true)

  // Computed properties
  const sectionCount = useMemo(() => notificationSections.length, [notificationSections])
  
  const enabledNotificationCount = useMemo(() => {
    if (!preferences) return 0
    
    return Object.values(preferences.sections).reduce((count, types) => {
      return count + types.length
    }, 0)
  }, [preferences])
  
  const hasAnyNotificationsEnabled = useMemo(() => {
    return globalEnabled && enabledNotificationCount > 0
  }, [globalEnabled, enabledNotificationCount])

  // Initialize data
  const initializeData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Load preferences and sections in parallel
      const [loadedPreferences, sections, permissionStatus] = await Promise.all([
        notificationsPageService.loadNotificationPreferences(),
        notificationsPageService.getDefaultSections(),
        notificationsPageService.checkNotificationPermissions()
      ])

      if (mountedRef.current) {
        setPreferences(loadedPreferences)
        setNotificationSections(sections)
        setGlobalEnabled(loadedPreferences.globalEnabled)
        setHasPermissions(permissionStatus)
        setPermissionChecked(true)
        setHasUnsavedChanges(false)
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load notification settings')
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [notificationsPageService])

  // Initialize on mount
  useEffect(() => {
    // Reset mountedRef on mount (fixes React Strict Mode double-render)
    mountedRef.current = true
    initializeData()

    return () => {
      mountedRef.current = false
    }
  }, [initializeData])

  // Update section preferences
  const updateSectionPreferences = useCallback(async (
    sectionId: string, 
    selectedTypes: NotificationType[]
  ) => {
    try {
      setError(null)
      
      const result = await notificationsPageService.updateSectionPreferences(sectionId, selectedTypes)
      
      if (result.success && result.preferences) {
        setPreferences(result.preferences)
        setGlobalEnabled(result.preferences.globalEnabled)
        setHasUnsavedChanges(false)
      } else {
        setError(result.error || 'Failed to update section preferences')
        setHasUnsavedChanges(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update section preferences')
      setHasUnsavedChanges(true)
    }
  }, [notificationsPageService])

  // Update all preferences
  const updateAllPreferences = useCallback(async (
    newPreferences: Record<string, NotificationType[]>
  ) => {
    try {
      setError(null)
      
      const result = await notificationsPageService.updateAllPreferences(newPreferences)
      
      if (result.success && result.preferences) {
        setPreferences(result.preferences)
        setGlobalEnabled(result.preferences.globalEnabled)
        setHasUnsavedChanges(false)
      } else {
        setError(result.error || 'Failed to update preferences')
        setHasUnsavedChanges(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update preferences')
      setHasUnsavedChanges(true)
    }
  }, [notificationsPageService])

  // Toggle notification type for section
  const toggleNotificationType = useCallback(async (
    sectionId: string, 
    type: NotificationType, 
    enabled: boolean
  ) => {
    if (!preferences) return

    const currentTypes = preferences.sections[sectionId] || []
    let newTypes: NotificationType[]

    if (enabled) {
      // Add the type if not already present
      newTypes = currentTypes.includes(type) ? currentTypes : [...currentTypes, type]
    } else {
      // Remove the type
      newTypes = currentTypes.filter(t => t !== type)
    }

    await updateSectionPreferences(sectionId, newTypes)
  }, [preferences, updateSectionPreferences])

  // Check if notification type is selected for section
  const isNotificationTypeSelected = useCallback((
    sectionId: string, 
    type: NotificationType
  ): boolean => {
    if (!preferences) return false
    return preferences.sections[sectionId]?.includes(type) || false
  }, [preferences])

  // Update global settings
  const updateGlobalSettings = useCallback(async (enabled: boolean) => {
    try {
      setError(null)
      
      const result = await notificationsPageService.updateGlobalSettings(enabled)
      
      if (result.success && result.preferences) {
        setPreferences(result.preferences)
        setGlobalEnabled(result.preferences.globalEnabled)
        setHasUnsavedChanges(false)
        
        // Update permissions if they were requested
        if (result.permissionRequested) {
          setHasPermissions(enabled)
        }
      } else {
        setError(result.error || 'Failed to update global settings')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update global settings')
    }
  }, [notificationsPageService])

  // Request notification permissions
  const requestPermissions = useCallback(async (): Promise<boolean> => {
    try {
      const granted = await notificationsPageService.requestNotificationPermissions()
      setHasPermissions(granted)
      setPermissionChecked(true)
      return granted
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request permissions')
      return false
    }
  }, [notificationsPageService])

  // Refresh permission status
  const refreshPermissions = useCallback(async () => {
    try {
      const permissionStatus = await notificationsPageService.checkNotificationPermissions()
      setHasPermissions(permissionStatus)
      setPermissionChecked(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check permissions')
    }
  }, [notificationsPageService])

  // Reset to default preferences
  const resetToDefaults = useCallback(async () => {
    try {
      setError(null)
      
      const result = await notificationsPageService.clearPreferences()
      
      if (result.success && result.preferences) {
        setPreferences(result.preferences)
        setGlobalEnabled(result.preferences.globalEnabled)
        setHasUnsavedChanges(false)
      } else {
        setError(result.error || 'Failed to reset preferences')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset preferences')
    }
  }, [notificationsPageService])

  // Validate current preferences
  const validateCurrentPreferences = useCallback((): boolean => {
    if (!preferences) return false
    return notificationsPageService.validatePreferences(preferences)
  }, [preferences, notificationsPageService])

  // Clear error state
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Refresh all data
  const refreshData = useCallback(async () => {
    await initializeData()
  }, [initializeData])

  // Get props for PageContentNotifications component
  const getPageContentNotificationsProps = useCallback((): Partial<PageContentNotificationsProps> => {
    const notificationSectionConfigs: NotificationSectionConfig[] = notificationSections.map(section => ({
      sectionName: section.name,
      notificationDescription: section.description,
      selectedTypes: preferences?.sections[section.id] || []
      // Note: We intentionally don't set per-section onChange here.
      // The component's onNotificationChange callback handles all updates
      // to avoid race conditions when multiple buttons are clicked quickly.
    }))

    return {
      notificationSections: notificationSectionConfigs,
      onNotificationChange: async (allPreferences: Record<string, ('Push' | 'Text' | 'Email')[]>) => {
        // Convert section names back to IDs for service layer
        const preferencesById: Record<string, NotificationType[]> = {}

        for (const [sectionName, types] of Object.entries(allPreferences)) {
          const section = notificationSections.find(s => s.name === sectionName)
          if (section) {
            preferencesById[section.id] = types
          }
        }

        await updateAllPreferences(preferencesById)
      },
      testID: 'notifications-page',
      accessibilityLabel: 'Notification preferences page'
    }
  }, [notificationSections, preferences, updateSectionPreferences, updateAllPreferences])

  return {
    // State
    preferences,
    notificationSections,
    loading,
    error,
    hasPermissions,
    permissionChecked,
    globalEnabled,
    hasUnsavedChanges,
    sectionCount,
    enabledNotificationCount,
    hasAnyNotificationsEnabled,
    
    // Actions
    updateSectionPreferences,
    updateAllPreferences,
    toggleNotificationType,
    isNotificationTypeSelected,
    updateGlobalSettings,
    requestPermissions,
    refreshPermissions,
    resetToDefaults,
    validateCurrentPreferences,
    clearError,
    refreshData,
    getPageContentNotificationsProps
  }
  }
}